import moment, { Moment } from 'moment'
import { SubscriptionDto } from '../types/profile.types'

const STORAGE_KEY = 'subscriptionExpiryNotifications'
const DATE_FORMAT = 'YYYY-MM-DD'

export const SUBSCRIPTION_EXPIRY_THRESHOLDS_DAYS = [7, 3, 1, 0]
export type SubscriptionExpiryThreshold = (typeof SUBSCRIPTION_EXPIRY_THRESHOLDS_DAYS)[number]

type StoredState = Record<
    string,
    {
        endDate: string
        shown: Partial<Record<SubscriptionExpiryThreshold, string>>
    }
>

export interface SubscriptionExpiryNotification {
    subscriptionId: string
    subscriptionName: string
    endDate: string
    daysLeft: number
    threshold: SubscriptionExpiryThreshold
}

function getStoredNotificationTimelines(): StoredState {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return {}
        return JSON.parse(raw) as StoredState
    } catch {
        return {}
    }
}

function saveNotificationTimeline(state: StoredState) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function normalizeEndDate(endDate: string): Moment {
    const endUtc = moment.utc(endDate)
    if (!endUtc.isValid()) return endUtc

    const endDateKey = endUtc.format(DATE_FORMAT)
    return moment(endDateKey, DATE_FORMAT, true)
}

function daysUntil(end: Moment, now: Moment): number {
    return end.clone().startOf('day').diff(now.clone().startOf('day'), 'days')
}

export function getSubscriptionExpiryNotification(
    subscription: SubscriptionDto | null | undefined,
    now: Moment = moment()
): SubscriptionExpiryNotification | null {
    if (!subscription || !subscription.isActiveSubscription) return null
    if (!subscription?.id || !subscription.endDate) return null

    const end = normalizeEndDate(subscription.endDate)
    if (!end.isValid()) return null

    const daysLeft = daysUntil(end, now)
    if (!SUBSCRIPTION_EXPIRY_THRESHOLDS_DAYS.includes(daysLeft as any)) return null
    const threshold = daysLeft as SubscriptionExpiryThreshold

    const storage = getStoredNotificationTimelines()
    const todayKey = now.format(DATE_FORMAT)

    const current = storage[subscription.id] ?? { endDate: subscription.endDate, shown: {} }
    const alreadyShownToday = current.shown?.[threshold] === todayKey
    if (alreadyShownToday) return null

    return {
        subscriptionId: subscription.id,
        subscriptionName: subscription.subscriptionName,
        endDate: subscription.endDate,
        daysLeft,
        threshold,
    }
}

export function markSubscriptionExpiryNotificationShown(
    subscriptionId: string,
    endDate: string,
    threshold: SubscriptionExpiryThreshold,
    now: Moment = moment()
) {
    if (!subscriptionId) return

    const storage = getStoredNotificationTimelines()
    const todayKey = now.format(DATE_FORMAT)

    const existing = storage[subscriptionId]
    const next = {
        endDate,
        shown: {
            ...(existing?.shown ?? {}),
            [threshold]: todayKey,
        },
    }

    storage[subscriptionId] = next
    saveNotificationTimeline(storage)
}
